import { useCallback, useEffect, useRef, useState } from 'react';
import { IconPlusBold } from '@divlab/divanui/icons';
import { cn, Discount, Price, Scroller } from '@divlab/divanui';

import useMedias from '@Hooks/useMedias';
import { useDeps } from '@Contexts/DI';
import { useMeta } from '@Queries/Meta';
import { useRequest } from '@Contexts/Request';
import * as HeaderStore from '@Stores/Header';
import * as PickPairStore from '@Stores/PickPair';
import * as ModalWindows from '@Stores/ModalWindows';
import getProductName from '@Utils/getProductName';
import BuyButton from '../BuyButton';
import Option from '../Option';
import styles from './Sidebar.module.css';

import type { FC, HTMLAttributes, MouseEvent } from 'react';
import type { ProductData, ProductPriceData } from '@Types/Product';
import type { PickPairData, SetData } from '@Api/Product/getSetItem';

interface Params {
  product: PickPairData;
  onApply?: (item: PickPairData) => void;
}

interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  set: SetData;
  product: ProductData;
}

const getParameterNames = (product: PickPairData) => {
  const paramsIds: string[] = [];

  product.parameters.forEach((parameter) => {
    parameter.items.forEach((value) => {
      if (value.every((val) => val.selected)) {
        const sizes = [];
        value.forEach((val) => {
          if (parameter.groupId === 2) {
            sizes.push(val.value?.toString());
          } else paramsIds.push(val.value?.toString());
        });
        if (sizes.length) {
          paramsIds.push(sizes.join('x') + parameter.unit);
        }
      }
    });
  });

  const selectedFabricsIds = product.fabrics.items
    .filter((fabric) => fabric.selected)
    .map((fabric) => fabric.name);

  return [...paramsIds, ...selectedFabricsIds];
};

const Sidebar: FC<SidebarProps> = (props) => {
  const { className, product, ...restProps } = props;
  const { isMobileM, isMobile } = useMedias();
  const { isUzb } = useRequest();
  const {
    data: { currency },
  } = useMeta();
  const refBuyButton = useRef<HTMLDivElement>(null);
  const refActions = useRef<HTMLDivElement>(null);
  const hasWarnings = PickPairStore.useHasWarnings();
  const { products, additional } = PickPairStore.useSet();
  const actions = PickPairStore.useActions();
  const hasScroll = !isMobile && additional.products?.length > 0;
  const { analytics } = useDeps();

  const { price } = PickPairStore.useProductInfo();

  const [currentPrice, setCurrentPrice] = useState<ProductPriceData>(product.price);

  useEffect(() => {
    if (price) setCurrentPrice(price);
  }, [price]);

  const hasExpired = currentPrice?.expired > 0;
  const hasDiscount = currentPrice?.discount > 0;

  const { totalHeight } = HeaderStore.useSizes();
  const [disabled, setDisabled] = useState(false);

  const handleCloseModal = useCallback(() => {
    analytics.dispatchEvent('landing.interact', {
      label: '',
      category: 'constructorComplect',
      action: 'close',
    });
  }, [analytics]);

  const handleClickOption = useCallback(
    (e: MouseEvent, params: Params) => {
      const { onApply } = params;

      ModalWindows.open('PickPairConstructor', {
        maxWidth: 645,
        product: params.product,
        onApply,
        isLanding: true,
        onClose: handleCloseModal,
      });

      analytics.dispatchEvent('landing.interact', {
        label: params.product.type.name,
        category: 'constructorComplect',
        action: 'open',
      });
    },
    [analytics, handleCloseModal],
  );

  const scrollToElem = useCallback(
    (elem: HTMLDivElement) => {
      const rect = elem.getBoundingClientRect();
      const top = window.scrollY + rect.top - totalHeight;

      window.scrollTo({ top, behavior: 'smooth' });
    },
    [totalHeight],
  );

  const handleBuyClick = useCallback(() => {
    if (!hasWarnings) return;

    if (isMobileM) {
      if (!refActions.current) return;

      scrollToElem(refActions.current);
    }

    setDisabled(true);

    setTimeout(() => {
      setDisabled(false);
    }, 1000);
  }, [isMobileM, hasWarnings, scrollToElem]);

  const handleApply = (data: PickPairData) => {
    const parameters = getParameterNames(data);
    const analyticsData = parameters.join('|');
    analytics.dispatchEvent('landing.interact', {
      label: analyticsData,
      category: 'constructorComplect',
      action: 'apply',
    });
  };

  return (
    <div
      {...restProps}
      className={cn(styles.wrapper, className, { [styles.hasScroll]: hasScroll })}
    >
      <div className={styles.container}>
        <Scroller className={styles.scroller}>
          <div className={styles.content}>
            <div className={styles.shortName}>{product.model}</div>
            <h1 className={styles.fullName} itemProp='name'>
              {getProductName(product)}
            </h1>

            <div className={styles.wrapperPrice}>
              <div
                className={styles.containerPrices}
                itemProp='offers'
                itemScope
                itemType='http://schema.org/Offer'
              >
                <meta itemProp='priceCurrency' content={currency} />
                <Price
                  className={cn(styles.actualPrice, {
                    [styles.promo]: product.promo,
                    [styles.longPrice]: isUzb,
                  })}
                  price={currentPrice?.actual}
                  itemProp='price'
                  content={currentPrice?.actual.toString()}
                />
                <link itemProp='availability' href='http://schema.org/InStock' />
                &nbsp;
                {hasExpired && (
                  <Price
                    expired
                    className={cn(styles.expiredPrice, { [styles.longPrice]: isUzb })}
                    price={currentPrice.expired}
                    withoutCurrency={isUzb}
                  />
                )}
                {hasDiscount && (
                  <Discount
                    className={cn(styles.discount, { [styles.longPrice]: isUzb })}
                    value={product.price.discount}
                    view='grayStroke'
                  />
                )}
              </div>
            </div>

            <div className={styles.actions} ref={refActions}>
              {products.map((item, index) => {
                return (
                  <Option
                    key={`products_${item.id}_${index}`}
                    product={item}
                    error={disabled}
                    onClickOption={(e) =>
                      handleClickOption(e, {
                        product: item,
                        onApply: (data) => {
                          handleApply(data);
                          PickPairStore.changeByType(data);
                        },
                      })
                    }
                    data-testid='option'
                  />
                );
              })}

              {additional.products.map((item, index) => (
                <Option
                  key={`additional_${item.id}_${index}`}
                  product={item}
                  canBeDeleted
                  error={disabled}
                  onClickOption={(e) =>
                    handleClickOption(e, {
                      product: item,
                      onApply: (data) => {
                        handleApply(data);
                        PickPairStore.changeAdditionalByIndex(data, index);
                      },
                    })
                  }
                  data-testid='additional-option'
                />
              ))}

              {actions.map((item, index) => (
                <div
                  key={`actions_${item.product.id}_${index}`}
                  className={cn(styles.addOption, { [styles.active]: item.product.isActive })}
                  onClick={(e) =>
                    handleClickOption(e, {
                      product: item.product,
                      onApply: (data) => {
                        handleApply(data);
                        PickPairStore.add(data);
                      },
                    })
                  }
                  data-testid='add-option'
                >
                  {item.title}
                  <IconPlusBold className={styles.addOptionIcon} />
                </div>
              ))}
            </div>
          </div>
        </Scroller>
      </div>
      <div className={styles.buttonWrapper} ref={refBuyButton} onClick={handleBuyClick}>
        <BuyButton className={styles.button} product={product} />
      </div>
    </div>
  );
};

export default Sidebar;
